import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { noWhitespaceOnlyValidator } from '../../../shared/validators/no-whitespace-only.validator';
import {
    RegistrationInvitationContext,
    RegistrationService
} from '../../../shared/services/registration-service/registration.service';
import { RegistrationDialogComponent } from '../registration-dialog/registration-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';

import { ActivatedRoute, Router } from '@angular/router';

const ENROLLMENT_GRANT_FAILED_MARKER =
    'Your account was created but access to the requested application could not be granted';

@Component({
    selector: 'app-registration-form',
    templateUrl: './registration-form.component.html',
    styleUrls: ['./registration-form.component.scss'],
    imports: [
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatDialogModule
    ]
})
export class RegistrationFormComponent implements OnInit {
    private router = inject(Router);
    formGroup: FormGroup;

    emailBeforeRegistration: string;
    invitationToken: string = '';
    invitationEmailLocked: boolean = false;
    invitationBlocked: boolean = false;
    recognizedInvitationToken: boolean = false;
    private pendingInvitationToken: string = '';

    bidManagementBaseUrl = environment.bidManagementBaseUrl;

    constructor(public dialog: MatDialog, private registrationService: RegistrationService, private route: ActivatedRoute) {
    }

    get enabled(): boolean {
        return !this.invitationBlocked
            && (this.formGroup.valid || this.formGroup.disabled)
            && (this.emailBeforeRegistration !== this.formGroup.get('email').value);
    }

    ngOnInit(): void {
        this.formGroup = new FormGroup({
            firstName: new FormControl('', [
                Validators.required,
                Validators.pattern('^[A-Za-z-.\', ]+$'),
                noWhitespaceOnlyValidator
            ]),
            lastName: new FormControl('',
                [
                    Validators.required,
                    Validators.pattern('^[A-Za-z-.\', ]+$'),
                    noWhitespaceOnlyValidator
                ]),
            email: new FormControl('', [
                Validators.required,
                Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')
            ]),
        });

        // Surface validation errors immediately on page load instead of waiting for the user to
        // interact with each field. Angular Material's default error state matcher gates mat-error
        // rendering on touched/dirty, so we mark everything touched up front. Validators were
        // already running silently; this just exposes their results to the UI.
        this.formGroup.markAllAsTouched();

        this.route.queryParamMap.subscribe((params) => {
            const token = params.get('token') || '';
            this.resetInvitationState();

            if (!token) {
                return;
            }

            this.pendingInvitationToken = token;
            this.registrationService.getInvitation(token).subscribe({
                next: (invitation) => this.handleInvitationContext(invitation, token),
                error: () => {
                    if (this.pendingInvitationToken !== token) {
                        return;
                    }

                    this.openInvitationErrorDialog('This registration link could not be loaded. Please try again later.');
                }
            });
        });
    }

    private handleInvitationContext(invitation: RegistrationInvitationContext, token: string): void {
        if (this.pendingInvitationToken !== token) {
            return;
        }

        if (invitation.used) {
            this.blockInvitation(
                'This registration link has already been used. If you have already enrolled, please sign in instead.'
            );
            return;
        }

        if (invitation.expired) {
            this.blockInvitation(
                'This registration link has expired. Please request a new invitation from your application administrator.'
            );
            return;
        }

        if (!invitation.valid) {
            this.blockInvitation(
                'This registration link is invalid. Please double-check the link in your invitation email.'
            );
            return;
        }

        this.invitationToken = token;
        this.formGroup.get('email').setValue(invitation.email);
        this.formGroup.get('email').disable();
        this.invitationEmailLocked = true;
        this.recognizedInvitationToken = true;

        // Pre-populate first and last name from the invitation when provided, but leave the
        // fields editable so the user can correct typos or confirm. The existing validators
        // (required, pattern, no-whitespace-only) still run as the user types.
        if (invitation.firstName) {
            this.formGroup.get('firstName').setValue(invitation.firstName);
        }

        if (invitation.lastName) {
            this.formGroup.get('lastName').setValue(invitation.lastName);
        }
    }

    private blockInvitation(message: string): void {
        this.invitationBlocked = true;
        this.formGroup.disable();
        this.openInvitationErrorDialog(message);
    }

    private resetInvitationState() {
        this.pendingInvitationToken = '';
        this.invitationToken = '';
        this.recognizedInvitationToken = false;
        this.invitationEmailLocked = false;
        this.formGroup.get('email').enable();
    }

    openInvitationErrorDialog(message: string) {
        this.dialog.open(RegistrationDialogComponent, {
            height: '250px',
            width: '500px',
            disableClose: true,
            data: {
                title: 'An error has occurred!',
                message
            }
        });
    }

    hasRequiredError(controlName: string) {
        return this.formGroup.get(controlName).hasError('required');
    }

    isInvalid(controlName: string) {
        return this.formGroup.get(controlName).invalid && !this.hasRequiredError(controlName);
    }

    @HostListener('window:beforeunload')
    preventClose() {
        if (this.formGroup.dirty) {
            return false;
        }
    }

    Register() {
        this.emailBeforeRegistration = this.formGroup.get('email').value;
        this.registrationService.registration({
            ...this.formGroup.getRawValue(),
            invitationToken: this.invitationToken
        }).subscribe((message: string) => {
                this.formGroup.markAsPristine();
                this.router.navigateByUrl('/registration-confirmation');
            },
            error => {
                this.formGroup.markAsPristine();

                if (this.isEnrollmentGrantFailed(error)) {
                    this.dialog.open(RegistrationDialogComponent, {
                        height: '250px', width: '500px',
                        disableClose: true,
                        data: {
                            title: 'Almost done',
                            message: '<b>Your account was created, but access to the requested application could not be granted at this time.</b><br><br>' +
                                'Please contact support so they can finish setting up your access.'
                        }
                    });
                    return;
                }

                if (error.status === 400) {  // User Account Already Exists
                    this.dialog.open(RegistrationDialogComponent, {
                        height: '250px', width: '500px',
                        disableClose: true,
                        data: {
                            title: 'Attention!',
                            message: '<b>This username is already associated with an account.</b><br><br>' +
                                'Please provide a different email address<br>' +
                                '<b>OR</b><br>' +
                                `Please log in using the username you provided <a href="${this.bidManagementBaseUrl}" target="_blank">here</a>`
                        }
                    });
                } else {
                    this.dialog.open(RegistrationDialogComponent, {
                        height: '250px', width: '500px',
                        disableClose: true,
                        data: {
                            title: 'An error has occurred!',
                            message: 'Please contact support and reference this error code: ' + error.error
                        }
                    });
                }
            }
        );
    }

    private isEnrollmentGrantFailed(error: { status?: number; error?: unknown }): boolean {
        if (error.status !== 502) {
            return false;
        }
        const body = typeof error.error === 'string' ? error.error : '';
        return body.includes(ENROLLMENT_GRANT_FAILED_MARKER);
    }
}
